// CustomTextInput.ts
import { Components } from 'formiojs';

// Define the component class
class CustomTextUrlFieldInput extends Components.components.url {
  static schema() {
    return Components.components.url.schema({
      type: 'customtextUrlFieldInput',
      label: 'Custom Text Url Field Input',
      key: 'customtextUrlFieldInput',
      input: true,
      placeholder: 'Please enter url',
      customProperty: 'default value'
    });
  }

  static get builderInfo() {
    return {
      title: 'Custom Text Url Field Input',
      group: 'basic',
      icon: 'text-width',
      weight: 70,
      schema: CustomTextUrlFieldInput.schema()
    };
  }

  // Override the render method to customize appearance
  render(element?: any): string {
    // Pass the element to the parent render method
    return super.render(element);
  }

  // Helper function to validate if a string is a valid URL
  isValidUrl(urlString: string): boolean {
    if (!urlString) return false;
    
    try {
      // Add protocol if missing to make the URL constructor work
      if (!/^https?:\/\//i.test(urlString)) {
        urlString = 'https://' + urlString;
      }
      
      const url = new URL(urlString);
      return url.hostname.includes('.');  // Basic check that it's likely a domain
    } catch (e) {
      return false;
    }
  }

  // Override attach method for custom event handling
  attach(element: any): Promise<any> {
    const result = super.attach(element);
    
    // Apply custom styling after attachment
    const inputElement = this.element.querySelector('input') as HTMLInputElement;
    if (inputElement) {
      // Create container for input and icon
      const container = document.createElement('div');
      container.style.position = 'relative';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      
      // Style the input to look like a hyperlink
      inputElement.style.border = '1px solid #007bff';
      inputElement.style.borderRadius = '.25rem';
      inputElement.style.padding = '6px 10px 6px 12px';
      inputElement.style.flexGrow = '1';
      inputElement.style.color = '#007bff';
      inputElement.style.outline = 'none';
      inputElement.style.backgroundColor = 'transparent';
      
      // Check if the input is disabled or readonly
      const isProtected = inputElement.disabled || inputElement.readOnly || this.disabled;
      
      // If disabled, we need to create a clickable overlay
      if (isProtected) {
        // Apply visual styling for disabled state
        inputElement.style.opacity = '1';
        inputElement.style.backgroundColor = '#e9ecef';
        inputElement.style.border = '1px solid #ced4da';
        // Create a clickable overlay div that will sit on top of the input
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.cursor = 'pointer';
        overlay.style.zIndex = '10'; // Higher z-index to ensure it's on top
        // Add a subtle indication that it's clickable
        overlay.title = 'Click to open URL';
        
        // Add the overlay to the container
        container.appendChild(overlay);
        
        // Add click handler to the overlay
        this.addEventListener(overlay, 'click', (event: Event) => {
          const value = this.dataValue;
          if (this.isValidUrl(value)) {
            // Open the URL
            let href = value;
            if (!/^https?:\/\//i.test(href)) {
              href = 'https://' + href;
            }
            // Prevent default behavior
            event.preventDefault();
            event.stopPropagation();
            // Open URL in new tab
            window.open(href, '_blank');
            return false;
          }
        }, true);
        
        // Add hover effect to the overlay
        this.addEventListener(overlay, 'mouseover', () => {
          if (this.isValidUrl(this.dataValue)) {
            inputElement.style.textDecoration = 'underline';
          }
        }, true);
        
        this.addEventListener(overlay, 'mouseout', () => {
          if (!this.isValidUrl(this.dataValue)) {
            inputElement.style.textDecoration = 'none';
          }
        }, true);
      }
      
      // Create URL icon
      const urlIcon = document.createElement('a');
      urlIcon.innerHTML = '<i class="external-link" aria-hidden="true"></i>';
      urlIcon.style.position = 'absolute';
      urlIcon.style.right = '10px';
      urlIcon.style.cursor = 'pointer';
      urlIcon.style.color = '#007bff';
      urlIcon.style.display = 'none'; // Hidden by default
      urlIcon.setAttribute('target', '_blank');
      urlIcon.setAttribute('rel', 'noopener noreferrer');
      urlIcon.title = 'Click to open URL';

      // Wrap the input in the container and add the icon
      inputElement.parentNode?.insertBefore(container, inputElement);
      container.appendChild(inputElement);
      container.appendChild(urlIcon);
      
      // Create a bound function for updating URL icon and styling
      const updateUrlIcon = () => {
        // Access value through the dataValue property of the component
        const value = this.dataValue;
        
        if (this.isValidUrl(value)) {
          urlIcon.style.display = 'inline';
          // Make sure URL has proper scheme
          let href = value;
          if (!/^https?:\/\//i.test(href)) {
            href = 'https://' + href;
          }
          urlIcon.href = href;
          // Add accessibility label for screen readers
          urlIcon.setAttribute('aria-label', `Open webpage ${value}`);
          // We'll handle tooltip display in the mouseover event instead of setting a static title
          // Add hyperlink styling
          if (document.activeElement !== inputElement) {
            inputElement.style.textDecoration = 'underline';
            inputElement.style.cursor = 'pointer';
          }
        } else {
          urlIcon.style.display = 'none';
          urlIcon.removeAttribute('href');
          urlIcon.removeAttribute('aria-label');
          // No need to clear title as we're not using it statically
          inputElement.style.textDecoration = 'none';
          inputElement.style.cursor = 'text';
        }
      };
      
      // Initial check
      updateUrlIcon();
      
      // Setup value change detection - several approaches to ensure it works
      
      // 1. Use Form.io's valueChange event
      this.addEventListener(this.element, 'formio.change', updateUrlIcon, true);
      
      // 2. Direct DOM event listeners for immediate feedback
      this.addEventListener(inputElement, 'input', updateUrlIcon, true);
      this.addEventListener(inputElement, 'change', updateUrlIcon, true);
      
      // 3. Watch for setValue calls
      const originalSetValue = this.setValue.bind(this);
      this.setValue = (value: any, flags: any) => {
        const result = originalSetValue(value, flags);
        updateUrlIcon();
        return result;
      };
      
      // Add hover effects for hyperlink-like behavior
      this.addEventListener(inputElement, 'mouseover', (event: MouseEvent) => {
        const isProtected = inputElement.disabled || inputElement.readOnly || this.disabled;
        
        if (document.activeElement !== inputElement && this.isValidUrl(this.dataValue)) {
          inputElement.style.textDecoration = 'underline';
          // Always use pointer cursor for valid URLs, even in protected mode
          inputElement.style.cursor = 'pointer';
          
          // Check if mouse is over the text content
          const text = this.dataValue;
          const textWidth = getTextWidth(text, getComputedStyle(inputElement).font);
          
          // Get mouse position relative to the input's content
          const rect = inputElement.getBoundingClientRect();
          const mouseX = event.clientX - rect.left;
          
          // Account for padding
          const paddingLeft = parseFloat(getComputedStyle(inputElement).paddingLeft);
          const mousePositionInContent = mouseX - paddingLeft;
          
          // If mouse is over the text content area, show tooltip
          if (mousePositionInContent <= textWidth + 10) {
            inputElement.title = 'Click to open URL';
          } else {
            inputElement.title = '';
          }
        } else if (!isProtected) {
          inputElement.style.cursor = 'text';
          inputElement.title = '';
        } else {
          // For protected fields without valid URLs
          inputElement.style.cursor = 'default';
          inputElement.title = '';
        }
      }, true);
      
      this.addEventListener(inputElement, 'mouseout', () => {
        const isProtected = inputElement.disabled || inputElement.readOnly || this.disabled;
        if (document.activeElement !== inputElement) {
          if (!this.isValidUrl(this.dataValue)) {
            inputElement.style.textDecoration = 'none';
          }
          
          // Set appropriate cursor based on state
          if (this.isValidUrl(this.dataValue)) {
            // Keep pointer for valid URLs
            inputElement.style.cursor = 'pointer';
          } else if (isProtected) {
            // Use default cursor for protected fields without valid URLs
            inputElement.style.cursor = 'default';
          } else {
            // Use text cursor for editable fields
            inputElement.style.cursor = 'text';
          }
          
          // Clear the title when mouse leaves the input
          inputElement.title = '';
        }
      }, true);
      
      // Add click handler to open URL when clicking on the text
      // This will only work when the input is not disabled
      this.addEventListener(inputElement, 'click', (event: Event) => {
        // Only proceed if we have valid URL and the input is not disabled
        // (this handler will not be triggered for disabled inputs, but we keep it for non-disabled inputs)
        if (this.isValidUrl(this.dataValue) && !inputElement.disabled) {
          const text = this.dataValue;
          const textWidth = getTextWidth(text, getComputedStyle(inputElement).font);
          
          // Get click position relative to the input's content
          // This accounts for padding and border
          const rect = inputElement.getBoundingClientRect();
          const clickX = (event as MouseEvent).clientX - rect.left;
          
          // Account for padding
          const paddingLeft = parseFloat(getComputedStyle(inputElement).paddingLeft);
          const clickPositionInContent = clickX - paddingLeft;
          
          // If clicked on the text content area (not padding/empty space)
          if (clickPositionInContent <= textWidth + 10) {
            // Prevent default behavior (focusing the input)
            event.preventDefault();
            event.stopPropagation();
            
            // Open the URL
            let href = text;
            if (!/^https?:\/\//i.test(href)) {
              href = 'https://' + href;
            }
            
            window.open(href, '_blank');
          }
        }
      }, true);
      
      // Function to calculate text width for determining click position
      function getTextWidth(text: string, font: string): number {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
          context.font = font;
          return context.measureText(text).width;
        }
        return 0;
      }
      
      // Add focus/blur listeners for styling
      this.addEventListener(inputElement, 'focus', () => {
        // In protected mode, we don't want to allow focus
        const isProtected = inputElement.disabled || inputElement.readOnly || this.disabled;
        if (isProtected) {
          inputElement.blur();
          return;
        }
        inputElement.style.textDecoration = 'none';
        inputElement.style.cursor = 'text';
      }, true);
      
      // Prevent keydown events in protected mode to disallow editing
      this.addEventListener(inputElement, 'keydown', (event: KeyboardEvent) => {
        const isProtected = inputElement.disabled || inputElement.readOnly || this.disabled;
        if (isProtected) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
      
      this.addEventListener(inputElement, 'blur', () => {
        updateUrlIcon(); // Apply appropriate styling based on URL validity
      }, true);
    }
    
    return result;
  }

  // Override the setValue method for proper UI updates
  setValue(value: any, flags: any) {
    const changed = super.setValue(value, flags);
    return changed;
  }
}

// Register the component
Components.addComponent('customtextinput', CustomTextUrlFieldInput);

export default CustomTextUrlFieldInput;